import { useState, useEffect, useCallback } from "react";
import api, {
  isOrganizationScopeCancel,
  settleOrganizationScopeCancel,
  withoutOrganizationScope,
} from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { useErrorToast } from "../hooks/useErrorToast";
import { Users as UsersIcon, Search, Shield, Building2, Plus, MoreVertical, Pencil, KeyRound, UserX, UserCheck, Eye, EyeOff, Trash2 } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";

// Every canonical role an admin may assign (Fleetora.Application/Common/Roles.cs's Roles.All).
// platform_admin is filtered out below for an organization_admin caller — AuthService.ValidateRoleAssignment
// would reject that assignment with a 403 anyway, so it's never offered as an option to that caller.
const ALL_ROLES = ["platform_admin", "organization_admin", "compliance_manager", "pilot", "operator", "viewer"];

const PLATFORM_ADMIN = "platform_admin";

const emptyFormData = (defaultOrgId = "") => ({
  email: "",
  name: "",
  password: "",
  role: "pilot",
  status: "active",
  organization_id: defaultOrgId,
});

// Same shape AuthService.NormalizeEmail enforces server-side — deliberately permissive, it only has to
// catch the typo before the request leaves, not to be an RFC 5322 parser.
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const UsersPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { activeOrganizationId, isPlatformAdmin } = useOrganizationScope();
  const showError = useErrorToast();

  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(emptyFormData());
  const [saving, setSaving] = useState(false);
  // Reset to hidden every time a dialog opens, so a password revealed for one user is never still
  // on screen when the next dialog opens.
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);

  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  const isOrgManager = [PLATFORM_ADMIN, "organization_admin"].includes(user?.role);

  // GET /organizations always returns every organization for platform_admin (or just the caller's own
  // org otherwise) regardless of the ambient X-Organization-Id scope — OrganizationsController.GetAll
  // never reads it — so this list needs no refetch when the active scope changes. It is sent with
  // withoutOrganizationScope() for exactly that reason: a plain `api.get` would still pick up the scope
  // generation and abort signal, so a scope switch mid-load would leave the promise permanently pending
  // (lib/api.js's deliberate never-settle) with no effect dependency to retry it — the org-name column
  // and the create combobox would then stay empty until the page remounts.
  const fetchOrganizations = useCallback(async () => {
    try {
      const res = await api.get('/organizations', withoutOrganizationScope());
      setOrganizations(res.data);
    } catch (error) {
      // Secondary data (org name column / create-dialog combobox) — a failure here shouldn't block the page.
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // GET /users, unlike /organizations, DOES read the ambient X-Organization-Id header (OI-067). This
  // page is deliberately NOT inside OrganizationScopeGate (it has to work in "all organizations" mode
  // too), so it gets none of the gate's key-remount cleanup and has to drop the stale rows itself —
  // otherwise a switch briefly shows the previous organization's users while the new fetch is in flight.
  //
  // Dialog state deliberately needs NO cleanup here, and this page needs no registerScopeChangeGuard
  // either, unlike Aircraft/Batteries/Documents: both dialogs are plain modal Radix dialogs (portal +
  // `fixed inset-0` overlay at z-index 9998, see components/ui/dialog.jsx), so while one is open the
  // organization picker in the header is behind the overlay and cannot be clicked. No code path changes
  // the scope on its own either — registerOrganizationScopeErrorHandler has no consumer outside
  // lib/api.js, and the context only resets the scope from its auth-change initialize effect. Clearing
  // dialogs on a scope change would therefore be guarding a transition that cannot happen.
  useEffect(() => {
    setUsers([]);
  }, [activeOrganizationId]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (error) {
      toast.error(t.users?.errorLoading || t.errors?.failedToLoadUsers || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
    // activeOrganizationId isn't read directly here — it drives the request via the ambient
    // X-Organization-Id header (lib/api.js), so it still has to be a dependency to force a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganizationId, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const getOrgName = (orgId) => {
    if (!orgId) return t.users?.platformLevel || "Platform Level";
    const org = organizations.find(o => o.id === orgId);
    return org?.company_name || "-";
  };

  const getRoleBadge = (role) => {
    const roleLabel = t.roles?.[role] || role;
    const roleConfig = {
      platform_admin: { className: "bg-purple-100 text-purple-800" },
      organization_admin: { className: "bg-sky-100 text-sky-800" },
      compliance_manager: { className: "bg-emerald-100 text-emerald-800" },
      pilot: { className: "bg-slate-100 text-slate-800" },
      operator: { className: "bg-amber-100 text-amber-800" },
      viewer: { className: "bg-gray-100 text-gray-700" },
    };
    const config = roleConfig[role] || { className: "bg-slate-100 text-slate-800" };
    return <Badge className={config.className}>{roleLabel}</Badge>;
  };

  const getStatusBadge = (status) => {
    if (status === "active") {
      return <Badge className="bg-emerald-100 text-emerald-800">{t.users?.statuses?.active || "Active"}</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-600">{t.users?.statuses?.inactive || "Inactive"}</Badge>;
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
  };

  const isSelf = (target) => !!target && target.id === user?.id;

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData(emptyFormData(isPlatformAdmin ? (activeOrganizationId || "") : ""));
    setShowNewUserPassword(false);
    setShowFormDialog(true);
  };

  const openEditDialog = (target) => {
    setEditingUser(target);
    setFormData({
      email: target.email,
      name: target.name,
      password: "",
      role: target.role,
      status: target.status,
      // A platform_admin target has no organization of its own; pre-seed the active scope so the
      // demotion picker below opens on a sensible default instead of empty.
      organization_id: target.organization_id || activeOrganizationId || "",
    });
    setShowFormDialog(true);
  };

  // Only SELF role/status changes are locked — AuthService rejects those with a flat 403 whatever the
  // target's role is. A platform_admin TARGET is no longer locked: UpdateUserRequest now carries
  // organization_id, so demoting one is a supported transition as long as the form supplies the
  // organization the account lands in.
  const roleLocked = !!editingUser && isSelf(editingUser);
  const statusLocked = !!editingUser && isSelf(editingUser);
  const assignableRoles = isPlatformAdmin ? ALL_ROLES : ALL_ROLES.filter(r => r !== PLATFORM_ADMIN);

  // The one update AuthService.UpdateUserAsync reads organization_id on. Everywhere else it is ignored
  // server-side (moving a user between organizations is a different operation), so the form never sends
  // it outside this transition.
  const willDemote = !!editingUser && !roleLocked
    && editingUser.role === PLATFORM_ADMIN && formData.role !== PLATFORM_ADMIN;
  // The mirror image: promotion always nulls the organization, so say so rather than leaving the old
  // value on screen as if it survived.
  const willPromote = !!editingUser && !roleLocked
    && editingUser.role !== PLATFORM_ADMIN && formData.role === PLATFORM_ADMIN;
  const needsOrganization = !editingUser && isPlatformAdmin && formData.role !== PLATFORM_ADMIN;
  // With an active scope the backend's ResolveOptionalCreateOrgId treats that scope as authoritative and
  // rejects any other organization in a create body, so the picker must not offer one — the free choice
  // belongs to "all organizations" mode. (No such rule applies to PUT, which is why the demotion picker
  // below stays a free list: locking it would reintroduce exactly the "can't get there from here"
  // friction this change exists to remove.)
  const createOrganizationLocked = needsOrganization && !!activeOrganizationId;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      toast.error(t.users?.validation?.nameRequired || "Name is required.");
      return;
    }

    if (editingUser) {
      if (willDemote && !formData.organization_id) {
        toast.error(t.users?.validation?.demotionOrganizationRequired
          || "Changing a platform administrator's role requires an organization to assign them to.");
        return;
      }

      // Partial payload: only send fields that actually changed. Resending an unchanged role/status
      // for a self-edit would otherwise trip AuthService's self-target 403 for no reason.
      const payload = {};
      if (trimmedName !== editingUser.name) payload.name = trimmedName;
      if (!roleLocked && formData.role !== editingUser.role) payload.role = formData.role;
      if (!statusLocked && formData.status !== editingUser.status) payload.status = formData.status;
      // Sent only with the demotion it belongs to — the backend ignores it on every other combination,
      // and sending it anyway would imply this form can move users between organizations.
      if (willDemote) payload.organization_id = formData.organization_id;

      if (Object.keys(payload).length === 0) {
        toast.info(t.messages?.noChanges || "No changes to save");
        setShowFormDialog(false);
        return;
      }

      setSaving(true);
      try {
        await api.put(`/users/${editingUser.id}`, payload, settleOrganizationScopeCancel());
        toast.success(t.users?.messages?.updated || "User updated");
        setShowFormDialog(false);
        fetchUsers();
      } catch (error) {
        // Scope changed mid-flight: the request was aborted and the scope effect already closed this
        // dialog, so there is nothing to report. The settle opt-in above is what makes this reachable at
        // all — without it lib/api.js never settles the promise, `finally` never runs and the dialog
        // stays stuck on "saving" forever.
        if (isOrganizationScopeCancel(error)) return;
        showError('failedToUpdate', error);
      } finally {
        setSaving(false);
      }
      return;
    }

    const trimmedEmail = formData.email.trim();
    if (!trimmedEmail) {
      toast.error(t.users?.validation?.emailRequired || "Email is required.");
      return;
    }
    // Mirrors AuthService.NormalizeEmail's own regex and 256-char column cap. Without this the request
    // reaches the backend and its rejection comes back as an English AppError.Validation message
    // regardless of the UI language (OI-056) — checking here keeps the common typo localized.
    if (!EMAIL_PATTERN.test(trimmedEmail) || trimmedEmail.length > 256) {
      toast.error(t.users?.validation?.emailInvalid || "A valid email address is required.");
      return;
    }
    if (formData.password.length < 6 || formData.password.length > 20) {
      toast.error(t.users?.validation?.passwordLength || "Password must be between 6 and 20 characters long.");
      return;
    }
    if (needsOrganization && !formData.organization_id) {
      toast.error(t.users?.validation?.organizationRequired || "Organization is required for this role.");
      return;
    }

    const payload = {
      email: trimmedEmail,
      password: formData.password,
      name: trimmedName,
      role: formData.role,
      organization_id: formData.role === PLATFORM_ADMIN ? null : (formData.organization_id || null),
    };

    setSaving(true);
    try {
      await api.post('/users', payload, settleOrganizationScopeCancel());
      toast.success(t.users?.messages?.created || "User created");
      setShowFormDialog(false);
      fetchUsers();
    } catch (error) {
      if (isOrganizationScopeCancel(error)) return;
      showError('failedToCreate', error);
    } finally {
      setSaving(false);
    }
  };

  // Deactivation is a status change (PUT), not a deletion — DELETE /api/users/{id} is now a real,
  // irreversible row removal (see AuthService.DeleteUserAsync), so it must never be reached from here.
  const handleDeactivate = async (target) => {
    if (!window.confirm(`${t.users?.actions?.deactivate || "Deactivate"} ${target.name} (${target.email})?`)) return;
    try {
      await api.put(`/users/${target.id}`, { status: "inactive" }, settleOrganizationScopeCancel());
      toast.success(t.users?.messages?.deactivated || "User deactivated");
      fetchUsers();
    } catch (error) {
      // No local flag to reset here, but the settle opt-in still matters: the scope effect's own
      // refetch is what repopulates the table, and a false error toast about the aborted request would
      // be misleading.
      if (isOrganizationScopeCancel(error)) return;
      showError('failedToUpdate', error);
    }
  };

  const handleReactivate = async (target) => {
    try {
      await api.put(`/users/${target.id}`, { status: "active" }, settleOrganizationScopeCancel());
      toast.success(t.users?.messages?.reactivated || "User reactivated");
      fetchUsers();
    } catch (error) {
      if (isOrganizationScopeCancel(error)) return;
      showError('failedToUpdate', error);
    }
  };

  // Permanent, unrecoverable row removal — deliberately a much stronger confirmation than deactivation
  // above, and no e-mail-retyping step (user explicitly asked for a plain, unambiguous warning instead).
  const handleDeleteUser = async (target) => {
    if (!window.confirm(t.users?.confirm?.deleteUser || "This will permanently and irreversibly delete this user account. This action cannot be undone. Continue?")) return;
    try {
      await api.delete(`/users/${target.id}`, settleOrganizationScopeCancel());
      toast.success(t.users?.messages?.deleted || "User deleted");
      fetchUsers();
    } catch (error) {
      if (isOrganizationScopeCancel(error)) return;
      showError('failedToDelete', error);
    }
  };

  const openPasswordDialog = (target) => {
    setPasswordTarget(target);
    setNewPassword("");
    setShowResetPassword(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6 || newPassword.length > 20) {
      toast.error(t.users?.validation?.passwordLength || "Password must be between 6 and 20 characters long.");
      return;
    }
    setResettingPassword(true);
    try {
      await api.put(`/users/${passwordTarget.id}/password`, { password: newPassword }, settleOrganizationScopeCancel());
      toast.success(t.users?.messages?.passwordReset || "Password reset");
      setPasswordTarget(null);
    } catch (error) {
      if (isOrganizationScopeCancel(error)) return;
      showError('resetPasswordFailed', error);
    } finally {
      setResettingPassword(false);
    }
  };

  if (!isOrgManager) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <Shield className="h-16 w-16 text-slate-300 mb-4" />
        <p className="text-lg font-medium text-slate-600">{t.common?.accessRestricted || "Access Restricted"}</p>
        <p className="text-sm text-slate-500">{t.users?.adminOnly || "Only administrators can view users"}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            {t.users?.title || "Users"}
          </h1>
          <p className="text-slate-500 mt-1">
            {user?.role === PLATFORM_ADMIN
              ? (t.users?.subtitle || "All platform users")
              : (t.users?.orgMembers || "Organization members")}
          </p>
        </div>
        <Button onClick={openCreateDialog} data-testid="add-user-button">
          <Plus className="h-4 w-4" /> {t.users?.actions?.createUser || "New User"}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={t.users?.searchPlaceholder || "Search users..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-users"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.users?.columns?.user || "User"}</TableHead>
                <TableHead>{t.users?.columns?.email || "Email"}</TableHead>
                <TableHead>{t.users?.columns?.role || "Role"}</TableHead>
                {user?.role === PLATFORM_ADMIN && <TableHead>{t.users?.columns?.organization || "Organization"}</TableHead>}
                <TableHead>{t.users?.columns?.status || "Status"}</TableHead>
                <TableHead className="text-right">{t.common?.actions || "Actions"}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <UsersIcon className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">{t.users?.emptyState || "No users found"}</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((u) => {
                  const self = isSelf(u);
                  return (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarFallback className="bg-slate-200 text-slate-600 text-sm">
                              {getInitials(u.name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{u.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                      <TableCell>{getRoleBadge(u.role)}</TableCell>
                      {user?.role === PLATFORM_ADMIN && (
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Building2 className="h-3 w-3" />
                            {getOrgName(u.organization_id)}
                          </div>
                        </TableCell>
                      )}
                      <TableCell>{getStatusBadge(u.status)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`user-actions-${u.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(u)} data-testid={`user-edit-${u.id}`}>
                              <Pencil className="h-4 w-4 mr-2" /> {t.actions?.edit || "Edit"}
                            </DropdownMenuItem>
                            {!self && (
                              <DropdownMenuItem onClick={() => openPasswordDialog(u)} data-testid={`user-reset-password-${u.id}`}>
                                <KeyRound className="h-4 w-4 mr-2" /> {t.users?.actions?.resetPassword || "Reset Password"}
                              </DropdownMenuItem>
                            )}
                            {!self && u.status === "active" && (
                              <DropdownMenuItem onClick={() => handleDeactivate(u)} className="text-red-600" data-testid={`user-deactivate-${u.id}`}>
                                <UserX className="h-4 w-4 mr-2" /> {t.users?.actions?.deactivate || "Deactivate"}
                              </DropdownMenuItem>
                            )}
                            {!self && u.status !== "active" && (
                              <DropdownMenuItem onClick={() => handleReactivate(u)} data-testid={`user-reactivate-${u.id}`}>
                                <UserCheck className="h-4 w-4 mr-2" /> {t.users?.actions?.reactivate || "Reactivate"}
                              </DropdownMenuItem>
                            )}
                            {!self && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleDeleteUser(u)} className="text-red-600" data-testid={`user-delete-${u.id}`}>
                                  <Trash2 className="h-4 w-4 mr-2" /> {t.users?.actions?.deleteUser || "Delete"}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={showFormDialog} onOpenChange={setShowFormDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? (t.users?.dialogs?.editTitle || "Edit User") : (t.users?.dialogs?.createTitle || "Create User")}
            </DialogTitle>
          </DialogHeader>
          {/* noValidate: every required field has its own localized check in handleSubmit instead of
              relying on the browser's own (unlocalized) constraint-validation bubbles. */}
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="user-form" noValidate>
            <div className="space-y-2">
              <Label htmlFor="user_name">{t.users?.fields?.name || "Name"} *</Label>
              <Input
                id="user_name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="user-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="user_email">{t.auth?.email || "Email"} *</Label>
              {editingUser ? (
                <Input id="user_email" value={formData.email} disabled data-testid="user-email" />
              ) : (
                <Input
                  id="user_email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="user-email"
                />
              )}
            </div>

            {!editingUser && (
              <div className="space-y-2">
                <Label htmlFor="user_password">{t.auth?.password || "Password"} *</Label>
                <div className="relative">
                  <Input
                    id="user_password"
                    type={showNewUserPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                    maxLength={20}
                    data-testid="user-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewUserPassword(!showNewUserPassword)}
                    aria-label={showNewUserPassword
                      ? (t.auth?.hidePassword || "Hide password")
                      : (t.auth?.showPassword || "Show password")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 transition-colors"
                    data-testid="user-password-toggle"
                  >
                    {showNewUserPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">{t.users?.validation?.passwordLength || "Password must be between 6 and 20 characters long."}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.users?.columns?.role || "Role"} *</Label>
                <Select
                  value={formData.role}
                  disabled={roleLocked}
                  onValueChange={(value) => setFormData({
                    ...formData,
                    role: value,
                    // platform_admin never has an organization; coming back from it restores the
                    // previously picked one, or the active scope's as a default.
                    organization_id: value === PLATFORM_ADMIN
                      ? ""
                      : (formData.organization_id || activeOrganizationId || ""),
                  })}
                >
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {assignableRoles.map((r) => (
                      <SelectItem key={r} value={r}>{t.roles?.[r] || r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {editingUser && (
                <div className="space-y-2">
                  <Label>{t.users?.columns?.status || "Status"} *</Label>
                  <Select
                    value={formData.status}
                    disabled={statusLocked}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="user-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{t.users?.statuses?.active || "Active"}</SelectItem>
                      <SelectItem value="inactive">{t.users?.statuses?.inactive || "Inactive"}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {(roleLocked || statusLocked) && (
              <p className="text-xs text-slate-500">{t.users?.selfActionHint || "You cannot change your own role or status."}</p>
            )}

            {/* Organization. Four cases, in order: the demotion picker (the one edit that carries an
                organization), the promotion notice (the organization is about to be dropped), any other
                edit (read-only — this form never transfers a user between organizations), and create. */}
            {willDemote ? (
              <div className="space-y-2">
                <Label htmlFor="user_org">{t.common?.organization || "Organization"} *</Label>
                <Select
                  value={formData.organization_id}
                  onValueChange={(value) => setFormData({ ...formData, organization_id: value })}
                >
                  <SelectTrigger data-testid="user-organization">
                    <SelectValue placeholder={t.placeholders?.selectOrganization || "Select organization"} />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>{org.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {t.users?.demotionOrgHint || "A platform administrator has no organization — pick the one this account moves into."}
                </p>
              </div>
            ) : willPromote ? (
              <div className="space-y-2">
                <Label>{t.common?.organization || "Organization"}</Label>
                <Input value={t.users?.platformLevel || "Platform Level"} disabled data-testid="user-organization-readonly" />
                <p className="text-xs text-amber-600" data-testid="user-promotion-notice">
                  {t.users?.promotionOrgNotice || "A platform administrator has no organization — this account will be removed from its current one."}
                </p>
              </div>
            ) : editingUser ? (
              <div className="space-y-2">
                <Label>{t.common?.organization || "Organization"}</Label>
                <Input value={getOrgName(editingUser.organization_id)} disabled data-testid="user-organization-readonly" />
              </div>
            ) : needsOrganization && (
              <div className="space-y-2">
                <Label htmlFor="user_org">{t.common?.organization || "Organization"} *</Label>
                {createOrganizationLocked ? (
                  <>
                    <Input value={getOrgName(activeOrganizationId)} disabled data-testid="user-organization-readonly" />
                    <p className="text-xs text-slate-500">
                      {t.users?.createOrgLockedHint || "New users are created in the active organization. Switch organizations in the header to create one elsewhere."}
                    </p>
                  </>
                ) : (
                  <Select
                    value={formData.organization_id}
                    onValueChange={(value) => setFormData({ ...formData, organization_id: value })}
                  >
                    <SelectTrigger data-testid="user-organization">
                      <SelectValue placeholder={t.placeholders?.selectOrganization || "Select organization"} />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map((org) => (
                        <SelectItem key={org.id} value={org.id}>{org.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowFormDialog(false)}>
                {t.actions?.cancel || t.common?.cancel || "Cancel"}
              </Button>
              <Button type="submit" disabled={saving} data-testid="user-form-submit">
                {t.actions?.save || "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!passwordTarget} onOpenChange={(open) => { if (!open) setPasswordTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.users?.dialogs?.resetPasswordTitle || "Reset Password"}</DialogTitle>
            {passwordTarget && (
              <DialogDescription>{passwordTarget.name} ({passwordTarget.email})</DialogDescription>
            )}
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="new_password">{t.users?.fields?.newPassword || "New Password"} *</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showResetPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  maxLength={20}
                  data-testid="new-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowResetPassword(!showResetPassword)}
                  aria-label={showResetPassword
                    ? (t.auth?.hidePassword || "Hide password")
                    : (t.auth?.showPassword || "Show password")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 transition-colors"
                  data-testid="new-password-toggle"
                >
                  {showResetPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500">{t.users?.validation?.passwordLength || "Password must be between 6 and 20 characters long."}</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPasswordTarget(null)}>
                {t.actions?.cancel || t.common?.cancel || "Cancel"}
              </Button>
              <Button type="submit" disabled={resettingPassword} data-testid="reset-password-submit">
                {t.users?.actions?.resetPassword || "Reset Password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
